import { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  useTheme,
  Typography,
  Fade,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { toast } from 'react-toastify';
import { createProject } from '../../services/projectService';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { addProject } from '../../state/slices/projectSlice';
import { useThemeContext } from '../../Context/ThemeContext';

const NewProjectModal = () => {
  const [open, setOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  const dispatch = useDispatch();
  const theme = useTheme();
  const { darkMode } = useThemeContext();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setProjectName('');
    setDescription('');
    setNameError('');
  };

  const handleSave = async () => {
    if (!projectName.trim()) {
      setNameError('Project name is required!');
      toast.error('Project name is required!', {
        position: 'top-right',
        autoClose: 2000,
      });
      return;
    }

    if (projectName.trim().length > 255) {
      setNameError('Project name must be 255 characters or less.');
      toast.error('Project name must be 255 characters or less.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);

    const userId = Cookies.get('userId');
    const newProject = {
      user_id: userId,
      name: projectName,
      description,
      is_deleted: false,
    };

    try {
      const savedProject = await createProject(newProject);
      dispatch(addProject(savedProject));
      toast.success('Project created successfully!', {
        position: 'top-right',
        autoClose: 2000,
      });
      handleClose();
    } catch (error) {
      console.log('Project creation error:', error.response?.data); // Debug log

      const errorResponse = error.response?.data;
      const errorCode = errorResponse?.error || errorResponse?.code;
      const errorDescription =
        errorResponse?.error_description ||
        errorResponse?.description ||
        errorResponse?.message ||
        errorResponse?.detail;
      const errorMessage =
        errorDescription ||
        error.message ||
        'An error occurred while creating the project';

      // Check for duplicate project name errors
      const isDuplicateName =
        errorCode === 'duplicate_project' ||
        errorCode === 'project_exists' ||
        errorMessage?.toLowerCase().includes('already exists') ||
        errorMessage?.toLowerCase().includes('duplicate') ||
        errorMessage?.toLowerCase().includes('project name') ||
        errorMessage?.toLowerCase().includes('name already') ||
        errorResponse?.name?.[0]?.toLowerCase().includes('already exists') ||
        errorResponse?.name?.[0]?.toLowerCase().includes('duplicate');

      // Handle duplicate project name errors
      if (isDuplicateName) {
        toast.error(
          'A project with this name already exists. Please use a different name.',
          {
            position: 'top-right',
            autoClose: 3000,
          },
        );
      }
      // Handle other types of errors
      else {
        // Try to extract a meaningful error message
        const displayMessage = errorMessage || 'Failed to create project';
        toast.error(displayMessage, {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        onClick={handleOpen}
        sx={{
          fontSize: '0.8',
          backgroundColor: '#5A66A6',
          padding: '0.3rem 0.7rem',
          color: 'white',
          textTransform: 'none',
          borderRadius: '6px',
          textWrap: 'nowrap',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          transition:
            'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms ease',
          willChange: 'transform, box-shadow',
          '&:hover': {
            backgroundColor: '#5965A6', // keep same color
            transform: 'translateY(-1px) scale(1.02)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
          },
          '&:active': {
            transform: 'translateY(0) scale(0.99)',
            boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
          },
        }}
      >
        + Create New
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={400}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(39, 34, 34, 0.64)',
            backdropFilter: 'blur(2px)',
          },
        }}
        PaperProps={{
          sx: {
            width: '500px',
            borderRadius: '16px',
            background: darkMode
              ? 'linear-gradient(135deg, #1a1a1c 0%, #2c2c2e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(79, 172, 254, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background:
                'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #4facfe 100%)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(79, 172, 254, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,

                color: darkMode ? '#ffffff' : '#1a202c',
                letterSpacing: '-0.02em',
              }}
            >
              Create New Project
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            overflowX: 'hidden',
            overflowY: 'auto',
            maxHeight: '65vh',
            p: 3,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: 16,
                fontWeight: 600,

                color: darkMode ? '#ffffff' : '#374151',
                mb: 1,
              }}
            >
              Project Name
            </Typography>

            <TextField
              label=""
              placeholder="Enter project name here"
              fullWidth
              variant="outlined"
              value={projectName}
              onChange={(e) => {
                const name = e.target.value;
                setProjectName(name);

                // Clear error when user starts typing
                if (nameError) {
                  setNameError('');
                }

                // Real-time validation for length
                if (name.trim().length > 255) {
                  setNameError('Project name must be 255 characters or less.');
                } else if (name.trim().length === 0) {
                  setNameError('');
                }
              }}
              error={!!nameError}
              helperText={nameError || `${projectName.length}/255 characters`}
              InputLabelProps={{
                style: { color: theme.palette.text.secondary },
              }}
              InputProps={{
                sx: {
                  '& .MuiInputBase-input': {
                    paddingTop: '12px',
                    paddingBottom: '12px',
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: darkMode ? '#2c2c2e' : '#ffffff',
                  border: '1px solid rgba(79, 172, 254, 0.1)',
                  boxShadow: darkMode
                    ? 'inset 0 1px 2px rgba(255, 255, 255, 0.04)'
                    : 'inset 0 1px 2px rgba(0, 0, 0, 0.06)',
                  '&:hover': {
                    borderColor: 'rgba(79, 172, 254, 0.3)',
                  },
                  '&.Mui-focused': {
                    borderColor: '#4facfe',
                    boxShadow: '0 0 0 3px rgba(79, 172, 254, 0.1)',
                  },
                  '&.Mui-error': {
                    borderColor: '#ef4444',
                  },
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.75rem',
                  mt: 0.5,
                  color: nameError
                    ? '#ef4444'
                    : darkMode
                      ? '#9ca3af'
                      : '#6b7280',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: 16,
                fontWeight: 600,

                color: darkMode ? '#ffffff' : '#374151',
                mb: 1,
              }}
            >
              Description
            </Typography>

            <TextField
              label=""
              placeholder="Enter project description (optional)"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              InputLabelProps={{
                style: { color: theme.palette.text.secondary },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: darkMode ? '#2c2c2e' : '#ffffff',
                  border: '1px solid rgba(79, 172, 254, 0.1)',
                  boxShadow: darkMode
                    ? 'inset 0 1px 2px rgba(255, 255, 255, 0.04)'
                    : 'inset 0 1px 2px rgba(0, 0, 0, 0.06)',
                  '&:hover': {
                    borderColor: 'rgba(79, 172, 254, 0.3)',
                  },
                  '&.Mui-focused': {
                    borderColor: '#4facfe',
                    boxShadow: '0 0 0 3px rgba(79, 172, 254, 0.1)',
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 4,
              gap: 2,
              pt: 2.5,
              borderTop: '1px solid rgba(79, 172, 254, 0.1)',
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',

                fontSize: '0.875rem',
                fontWeight: 500,
                px: 3,
                borderColor: 'rgba(79, 172, 254, 0.59)',
                color: '#4facfe',
                '&:hover': {
                  borderColor: '#4facfe',
                  background: 'rgba(79, 172, 254, 0.05)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!projectName.trim() || loading}
              variant="contained"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',

                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                background: !projectName.trim()
                  ? darkMode
                    ? '#444'
                    : '#ccc'
                  : 'linear-gradient(90deg, #4facfe, #00d4ff)',
                color: !projectName.trim()
                  ? darkMode
                    ? '#888'
                    : '#666'
                  : '#ffffff',
                '&:hover': {
                  background: !projectName.trim()
                    ? darkMode
                      ? '#444'
                      : '#ccc'
                    : 'linear-gradient(90deg, #3b9cfe, #00c4ef)',
                  transform: projectName.trim() ? 'scale(1.02)' : 'none',
                },
                '&:disabled': {
                  background: darkMode ? '#444' : '#ccc',
                  color: darkMode ? '#888' : '#666',
                },
              }}
            >
              {loading ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default NewProjectModal;
